import React, { useState, useEffect } from 'react';
import { 
    Clock, CheckCircle, AlertTriangle, Save, Settings 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const ParametreHoraire = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    heure_check_in_standard: '14:00', 
    heure_limite_check_in: '23:59', 
    heure_check_out_standard: '12:00',
    duree_max_day_use_heures: 6
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchParametres = async () => {
    try {
      setLoading(true);
      const res = await API.get('/hebergement/parametres');
      if (res.data) {
        setFormData({
          heure_check_in_standard: res.data.heure_check_in_standard || '14:00',
          heure_limite_check_in: res.data.heure_limite_check_in || '23:59',
          heure_check_out_standard: res.data.heure_check_out_standard || '12:00',
          duree_max_day_use_heures: res.data.duree_max_day_use_heures ?? 6
        });
      }
    } catch (err) {
      console.error("Erreur chargement paramètres hôteliers:", err);
      showToast("Erreur de chargement des paramètres horaires", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParametres();
  }, [company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.heure_check_in_standard || !formData.heure_limite_check_in || !formData.heure_check_out_standard || formData.duree_max_day_use_heures === undefined || formData.duree_max_day_use_heures === null) {
      showToast("Tous les champs sont obligatoires", "error");
      return;
    }

    setLoading(true);
    try {
      await API.put('/hebergement/parametres', formData);
      showToast("Paramètres horaires mis à jour avec succès");
      fetchParametres();
    } catch (err) {
      console.error("Erreur mise à jour paramètres :", err);
      const errorMsg = err.response?.data?.error || "Erreur lors de la mise à jour";
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', background: '#f1f5f9', position: 'relative', overflow: 'hidden' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Clock size={22} color="#2563eb" /> PARAMÈTRES HORAIRES DE L'HÉBERGEMENT
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Définition des règles horaires standards pour les check-ins, check-outs, Day Use et gestion des No-Shows.
            </div>
          </div>
        </header>

        <div style={{ flex: 1, padding: '30px 40px', overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>
          <div style={{ ...contentCardStyle, padding: '30px', width: '100%', maxWidth: '1200px', height: 'fit-content' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
              <Settings size={18} color="#2563eb" />
              <h3 style={{ fontSize: '15px', fontWeight: '800', margin: 0, color: '#1e293b' }}>
                Configuration des plages horaires et du Day Use de l'établissement
              </h3>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>HEURE STANDARD DE CHECK-IN (Arrivée)</label>
                  <input 
                    type="time" 
                    style={inputStyle} 
                    value={formData.heure_check_in_standard} 
                    onChange={(e) => setFormData({...formData, heure_check_in_standard: e.target.value})} 
                    required 
                  />
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Heure à partir de laquelle les chambres sont prêtes pour l'accueil des clients.
                  </small>
                </div>

                <div>
                  <label style={labelStyle}>HEURE LIMITE DE CHECK-IN (No-Show)</label>
                  <input 
                    type="time" 
                    style={inputStyle} 
                    value={formData.heure_limite_check_in} 
                    onChange={(e) => setFormData({...formData, heure_limite_check_in: e.target.value})} 
                    required 
                  />
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Heure seuil au-delà de laquelle une réservation non honorée peut basculer en No-Show.
                  </small>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>HEURE STANDARD DE CHECK-OUT (Départ)</label>
                  <input 
                    type="time" 
                    style={inputStyle} 
                    value={formData.heure_check_out_standard} 
                    onChange={(e) => setFormData({...formData, heure_check_out_standard: e.target.value})} 
                    required 
                  />
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Heure maximale à laquelle le client doit libérer la chambre (au-delà, application possible de frais de Late Check-out).
                  </small>
                </div>

                <div>
                  <label style={labelStyle}>DURÉE MAXIMALE DAY USE (en heures)</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="24"
                    style={inputStyle} 
                    value={formData.duree_max_day_use_heures} 
                    onChange={(e) => setFormData({...formData, duree_max_day_use_heures: parseInt(e.target.value) || 0})} 
                    required 
                  />
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Nombre d'heures maximum autorisé pour un séjour de type Day Use à compter de l'arrivée (ex: 5h, 6h).
                  </small>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <button type="submit" disabled={loading} style={btnSubmitStyle}>
                  <Save size={16} /> {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </main>

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES HARMONISÉS ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', marginTop: '5px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' };
const btnSubmitStyle = { padding: '0 25px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', height: '45px', display: 'flex', alignItems: 'center', gap: '8px' };
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

export default ParametreHoraire;